import { ReactNode, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetTenant } from '@/api/trafikportalen';
import { ChangelogDialog } from '@/components/ChangelogDialog';
import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { soleLink } from '@/helpers/soleLink';
import type { FaqAudience } from '@/helpers/visibleFaqs';
import { useTenantID } from '@/hooks/useTenantID';
import { useTutorial } from '@/hooks/useTutorial';
import { useVisibleFaqAudiences } from '@/hooks/useVisibleFaqAudiences';
import { IconExternalLink } from '@/icons';
import type { FileRoutesByTo } from '@/routeTree.gen';

const faqMarkdown = (
  audience: FaqAudience | undefined,
  company: string | undefined,
  school: string | undefined,
) => {
  if (audience === 'company') return company;
  if (audience === 'school') return school;
  return undefined;
};

export function SecondaryMenu() {
  const { formatMessage } = useIntl();
  const { startTutorial } = useTutorial();
  const tenantId = useTenantID();
  const navigate = useNavigate();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const { data: tenant } = useGetTenant();
  const audiences = useVisibleFaqAudiences();
  const singleFaq = audiences?.length === 1 ? audiences[0] : undefined;
  const faqHref = soleLink(
    faqMarkdown(singleFaq, tenant?.faq_company, tenant?.faq_school),
  )?.href;

  const handleStartTutorial = () => {
    const currentPath = window.location.pathname;

    if (currentPath !== `/${tenantId}`) {
      navigate({ to: '/$tenantId', params: { tenantId } });
    }
    startTutorial();
  };

  return (
    <nav
      aria-label="Hjälp och information"
      className="mt-10 flex flex-col gap-1 text-text-800"
    >
      <button
        onClick={handleStartTutorial}
        className="hidden rounded p-0 text-left outline-none hover:underline focus:outline-none focus-visible:shadow-[0_0_0_3px_white] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 lg:block"
      >
        <FormattedMessage defaultMessage="Visa guide" />
      </button>
      <ViewPermissions permission="tenant:viewer">
        <SecondaryMenuItem
          to="/$tenantId/portal-info"
          dataTutorial="portal-info"
        >
          <FormattedMessage defaultMessage="Portalinformation" />
        </SecondaryMenuItem>
      </ViewPermissions>
      {faqHref ? (
        <ExternalHelpLink
          href={faqHref}
          name={formatMessage({ defaultMessage: 'FAQ' })}
        />
      ) : (
        <SecondaryMenuItem to="/$tenantId/faq">
          <FormattedMessage defaultMessage="FAQ" />
        </SecondaryMenuItem>
      )}
      <HelpPageLink
        to="/$tenantId/support"
        markdown={tenant?.support}
        name={formatMessage({ defaultMessage: 'Support' })}
      />
      <HelpPageLink
        to="/$tenantId/privacy-policy"
        markdown={tenant?.privacy_policy}
        name={formatMessage({ defaultMessage: 'Integritetspolicy' })}
      />
      <HelpPageLink
        to="/$tenantId/terms-and-conditions"
        markdown={tenant?.terms_and_conditions}
        name={formatMessage({ defaultMessage: 'Allmänna villkor' })}
      />
      <a
        href="/accessibility"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Tillgänglighet (öppnas i ny flik)"
        className="inline-flex items-center hover:underline"
      >
        <FormattedMessage defaultMessage="Tillgänglighet" />
        <IconExternalLink className="ml-2 size-4" aria-hidden="true" />
      </a>
      <button
        onClick={() => setChangelogOpen(true)}
        className="rounded p-0 text-left outline-none hover:underline focus:outline-none focus-visible:shadow-[0_0_0_3px_white] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
      >
        <FormattedMessage defaultMessage="Ändringslogg" />
      </button>
      <ChangelogDialog open={changelogOpen} onOpenChange={setChangelogOpen} />
    </nav>
  );
}

function HelpPageLink({
  to,
  markdown,
  name,
}: {
  to: keyof FileRoutesByTo;
  markdown?: string;
  name: string;
}) {
  const href = soleLink(markdown)?.href;
  if (href) {
    return <ExternalHelpLink href={href} name={name} />;
  }

  return <SecondaryMenuItem to={to}>{name}</SecondaryMenuItem>;
}

function ExternalHelpLink({ href, name }: { href: string; name: string }) {
  const { formatMessage } = useIntl();

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={formatMessage(
        { defaultMessage: '{name} (öppnas i ny flik)' },
        { name },
      )}
      className="inline-flex items-center hover:underline"
    >
      {name}
      <IconExternalLink className="ml-2 size-4" aria-hidden="true" />
    </a>
  );
}

function SecondaryMenuItem({
  to,
  children,
  dataTutorial,
}: {
  to: keyof FileRoutesByTo;
  children: ReactNode;
  dataTutorial?: string;
}) {
  const tenantId = useTenantID();
  return (
    <Link
      className="hover:underline"
      to={to}
      params={{ tenantId }}
      data-tutorial={dataTutorial}
    >
      {children}
    </Link>
  );
}
